import { useState } from 'react'
import { useRouter } from 'next/router'
import Joyride, { Placement, Step } from 'react-joyride'

import axios from 'axios'
import Cookies from 'js-cookie'
import { addDays, endOfDay, isToday, addMinutes, startOfDay } from 'date-fns'
import useSWR, { SWRConfiguration } from 'swr'

import Step1Content from 'src/components/common/export-component/steps/one'
import Step2Content from 'src/components/common/export-component/steps/two'
import { ExportConfigStep1, Column, DownloadReportConfig } from 'src/components/common/export-component/types'

import { apiURI, constants, dashboardTabs } from 'src/lib/utils/constants'
import { postRequest } from 'src/lib/core/api-client'
import { ParentComponentTypes } from 'src/lib/types/activityLogTypes'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { useLocale } from 'src/lib/hooks/useLocal'
import { formatDateToUTC, toUTC, toUTCTimeStamp } from 'src/lib/utils/helpers'
import { exportReport } from 'src/lib/core/api-methods'

import JoyrideTooltip from 'src/components/common/export-component/steps/Tooltip'
import { useUser } from 'src/lib/contexts/UserContext'
import { useACRPermissions } from 'src/lib/access-permission-hooks/useACRPermissions'
import { useOrders } from 'src/lib/access-permission-hooks/useOrderPermissions'
import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@shopflo/ui'
import { ArrowRight, CaretRight, X } from '@phosphor-icons/react'
import useGTM from 'src/lib/hooks/useGTM'
import { ReportProvider } from 'src/contexts/ReportContext'

const fetcher: SWRConfiguration<Column[]>['fetcher'] = async (url: string) => {
  const response = await axios.get(url, {
    baseURL: apiURI.CHECKOUT_NODE,
    headers: { Authorization: Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? '' }
  })
  return response.data.data.columns
}

const downloadReportSteps: Step[] = [
  {
    target: '.nav-download-report',
    content: 'One new report added',
    disableBeacon: true,
    placement: 'right' as Placement,
    floaterProps: {
      hideArrow: true
    }
  }
]

const ExportConfigurationDialog = ({ type }: { type: ParentComponentTypes }) => {
  const { t } = useLocale()
  const router = useRouter()
  const { user } = useUser()
  const { sendGTMTrigger } = useGTM()

  const isOrders = router.query['open-report-filter'] === 'orders'
  const isAbandonedCheckout = router.query['open-report-filter'] === 'abandoned-checkout'
  const isAllCheckout = router.query['open-report-filter'] === 'all-checkouts'

  const { canExportACRData } = useACRPermissions()
  const { canExportOrdersData } = useOrders()

  const exportType =
    router.pathname === '/orders'
      ? 'orders'
      : router.pathname === '/abandoned-checkouts'
      ? 'abandoned_checkouts'
      : 'all_checkouts'

  const [downloadReportStepsRun, setDownloadReportStepsRun] = useState(false)

  const [open, setOpen] = useState(isOrders || isAbandonedCheckout || isAllCheckout)
  const [step, setStep] = useState(1)
  const [isStepValid, setIsStepValid] = useState(true)
  const [config, setConfig] = useState<DownloadReportConfig>({
    step1: {
      exportType,
      selectedColumns: []
    },
    step2: {
      exportFrequency: 'once',
      dateRange: {
        startDate: addDays(new Date(), -1).toISOString(),
        endDate: addDays(new Date(), -1).toISOString()
      },
      schedule: {
        frequency: 'daily',
        hour: 0,
        day: 0,
        cronExpression: '0 0 * * ? *',
        scheduledStartDate: new Date().toISOString()
      },
      emails: [''],
      reportName: ''
    }
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const isExportingTodaysReport = config.step1.exportType === 'today'

  const { data, error, isValidating } = useSWR<Column[]>(
    isExportingTodaysReport ? null : `dashboard/report/column-list?type=${config.step1.exportType}`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnMount: true
    }
  )

  const handleTodayExport = async () => {
    try {
      if (!config.step2.email) {
        errorToast(t('report.download.failed'))
        return
      }
      setIsSubmitting(true)
      const response: any = await exportReport(
        exportType === 'abandoned_checkouts' ? 'all_checkouts' : exportType,
        config.step2.email,
        toUTCTimeStamp(startOfDay(new Date())),
        toUTCTimeStamp(endOfDay(new Date()))
      )
      if (response?.error) {
        setIsSubmitting(false)
        return
      }
      successToast(t('report.download.success.today'))
      resetState()
    } catch (error) {
      errorToast(t('report.download.failed'))
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetState = () => {
    setStep(1)
    setIsStepValid(false)
    setConfig({
      step1: {
        exportType:
          router.pathname === '/orders'
            ? 'orders'
            : router.pathname === '/abandoned-checkouts'
            ? 'abandoned_checkouts'
            : 'all_checkouts',
        selectedColumns: []
      },
      step2: {
        exportFrequency: 'once',
        dateRange: {
          startDate: addDays(new Date(), -1).toISOString(),
          endDate: new Date().toISOString()
        },
        schedule: {
          frequency: 'daily',
          hour: 0,
          day: 0,
          cronExpression: '0 0 * * ? *',
          scheduledStartDate: new Date().toISOString()
        },
        emails: [''],
        reportName: ''
      }
    })
  }

  const handleSubmit = async () => {
    if (isExportingTodaysReport) {
      await handleTodayExport()
      return
    }

    try {
      setIsSubmitting(true)
      const payload: {
        name: string
        frequencyType: string
        columns: readonly string[]
        type: ExportConfigStep1['exportType']
        reportDateRange?: {
          start: string
          end: string
        }
        emails?: string[]
        scheduleStartDate?: string
        frequency?: string
      } = {
        name: config.step2.reportName,
        frequencyType: config.step2.exportFrequency.toUpperCase(),
        columns: config.step1.selectedColumns,
        type: config.step1.exportType
      }

      // handle emails
      if (config.step2.emails.length > 0) {
        payload.emails = config.step2.emails
      }

      // handle date range for once
      if (
        config.step2.exportFrequency === 'once' &&
        config.step2.dateRange.startDate &&
        config.step2.dateRange.endDate
      ) {
        payload.reportDateRange = {
          start: formatDateToUTC(addDays(new Date(config.step2.dateRange.startDate), -1)),
          end: formatDateToUTC(config.step2.dateRange.endDate)
        }
      }

      // handle recurring schedule
      if (config.step2.exportFrequency === 'recurring' && config.step2.schedule?.scheduledStartDate) {
        const scheduledDate = new Date(config.step2.schedule.scheduledStartDate)

        if (isToday(scheduledDate)) {
          // If scheduled date is today, set time to current time + 5 minutes
          const futureDate = addMinutes(new Date(), 5)
          payload.scheduleStartDate = toUTC(futureDate.toISOString())
        } else {
          payload.scheduleStartDate = toUTC(config.step2.schedule.scheduledStartDate)
        }

        payload.frequency = config.step2.schedule?.cronExpression
      }

      await postRequest(`/dashboard/report/scheduler-create`, apiURI.CHECKOUT_NODE, payload)

      if (exportType === 'orders') {
        sendGTMTrigger('orders_export_report_generated', 'create', {
          exportType: config.step1.exportType
        })
      }
      if (exportType === 'abandoned_checkouts') {
        sendGTMTrigger('abandoned_checkouts_export_report_generated', 'create', {
          exportType: config.step1.exportType
        })
      }
      successToast(t('report.download.success'))
      setDownloadReportStepsRun(true)

      // reset the states
      resetState()
    } catch (error) {
      errorToast(t('report.download.failed'))
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleNext = async () => {
    if (!isStepValid && !isExportingTodaysReport) return

    if (step === 1) {
      setStep(2)
    } else {
      await handleSubmit()
      setOpen(false)
    }
  }

  const handleBack = () => {
    if (step === 2) {
      setStep(1)
    } else {
      setOpen(false)
    }
  }

  const isExportDisabled =
    (type === dashboardTabs.FLO_CHECKOUT_TAB && !canExportACRData) ||
    (type === dashboardTabs.FLO_ORDERS_TAB && !canExportOrdersData)

  if (isExportDisabled) {
    return null
  }

  return (
    <ReportProvider>
      <Joyride
        run={downloadReportStepsRun}
        steps={downloadReportSteps}
        disableOverlay
        tooltipComponent={props => <JoyrideTooltip {...props} onDismiss={() => setDownloadReportStepsRun(false)} />}
      />
      <Button
        variant='outline'
        size='md'
        loading={isValidating}
        id='export-button'
        disabled={!isExportingTodaysReport && (!data || error)}
        onClick={() => setOpen(true)}
        rightIcon={ArrowRight}
      >
        <p className='text-text-sm-semibold'>{t('report.download.title')}</p>
      </Button>

      <Dialog open={open} onOpenChange={() => setOpen(!open)}>
        <DialogContent className='min-w-[600px] !rounded-2xl bg-white shadow-lg'>
          <DialogHeader className='sticky top-0 bg-white z-50 flex flex-row justify-between items-start h-fit'>
            <div className='flex flex-col gap-1 h-fit'>
              <DialogTitle className='text-text-lg-semibold text-gray-900'>
                {step === 1 ? 'Select columns to export' : 'Finalise export'}
              </DialogTitle>
              <p className='text-text-xs-medium text-zinc-500'>Step {step} of 2</p>
            </div>
            <IconButton IconComponent={X} size='sm' variant='ghost' onClick={() => setOpen(false)} />
          </DialogHeader>

          <div>
            {step === 1 ? (
              <Step1Content
                isLoading={isValidating}
                columns={data ?? []}
                config={config.step1}
                onChange={newConfig => setConfig(prev => ({ ...prev, step1: newConfig }))}
                onValidationChange={setIsStepValid}
              />
            ) : (
              <Step2Content
                step1Config={config.step1}
                config={config.step2}
                onChange={newConfig => setConfig(prev => ({ ...prev, step2: newConfig }))}
                onValidationChange={setIsStepValid}
              />
            )}
          </div>

          <DialogFooter className='bottom-0 bg-white z-50 flex flex-row justify-between items-center p-0 mt-2'>
            <Button onClick={handleBack} size='md' variant='outline'>
              {step === 1 ? 'Cancel' : 'Go back'}
            </Button>
            <Button
              id='get-report-button'
              variant='primary'
              loading={isSubmitting}
              onClick={handleNext}
              disabled={!isStepValid || user?.isDemoUser}
              type={step === 1 ? 'button' : 'submit'}
              size='md'
            >
              {step === 1 ? 'Continue' : 'Get report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ReportProvider>
  )
}

export default ExportConfigurationDialog
